/**
 * Client-Side Institutions API
 *
 * This module provides client-side API functions for institution operations
 * to be used with React Query.
 */

import { directApiClient } from '@/lib/api/direct-client';
import type {
  Institution,
  MatterInstitutionsListResponse,
  MatterInstitution
} from './types';

/**
 * The legacy `GET /matters/:matterId/institutions` returns global `Institution`
 * rows (with stages) — and, since LF-4022, the relationship-row PK projected on
 * as `matterInstitutionId`. We surface that PK so callers can record a
 * collection (payout) against the correct `MatterInstitution`.
 */
type MatterInstitutionApiItem = Institution & {
  matterInstitutionId?: number | null;
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get all institutions for a matter
 * Backend returns Institution[] directly, not MatterInstitution[]
 */
export async function getMatterInstitutions(
  matterId: number
): Promise<MatterInstitutionsListResponse> {
  // Backend returns global Institution rows (with stages + matterInstitutionId)
  const institutionsArray = await directApiClient.get<
    MatterInstitutionApiItem[]
  >(`/matters/${matterId}/institutions`);

  // Transform to MatterInstitution[] format. `institutionId` is the global id
  // (what the drawer keys on); `matterInstitutionId` is the relationship-row PK
  // the collection (payout) endpoint needs — resolved by matching the global id.
  const institutions: MatterInstitution[] = (institutionsArray || []).map(
    (inst) => ({
      id: inst.id,
      matterId: matterId,
      institutionId: inst.id,
      matterInstitutionId: inst.matterInstitutionId ?? null,
      institution: inst,
      isPrimary: false, // This info is not in the response
      notes: undefined, // Use undefined instead of null for optional field
      createdAt: inst.createdAt || new Date().toISOString(),
      updatedAt: inst.updatedAt || new Date().toISOString()
    })
  );

  const result = {
    institutions: institutions,
    total: institutions.length
  };

  return result;
}
